import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Select } from '@/components/ui';
import { createDeal, listCompanies, updateDeal } from '@/data/client';
import { useMembers } from '@/data/hooks';
import { useMe } from '@/auth/useMe';
import type { Deal, DealDetail } from '@/data/types';
import { FormError, Modal } from './Modal';

const s = (n: number | null | undefined) => (n == null ? '' : String(n));

/* New deal on a company (no lead → unattributed), or edit an existing deal's
   values. Margin appears only when the workspace tracks it. */
export function DealFormModal({ deal, contacts, companyId, onClose }: {
  deal?: Deal; contacts?: DealDetail['companyContacts']; companyId?: string; onClose: () => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { me } = useMe();
  const { ownerOptions } = useMembers();
  const { data: companies = [] } = useQuery({ queryKey: ['companies', {}], queryFn: () => listCompanies(), enabled: !deal && !companyId });
  const [f, setF] = useState({
    companyId: companyId ?? '', name: deal?.name ?? '', mrr: s(deal?.mrr), setupFee: s(deal?.setupFee),
    contractMonths: s(deal?.contractMonths), grossMarginPct: s(deal?.grossMarginPct), strategyFeeAmount: s(deal?.strategyFeeAmount),
    ownerUserId: deal?.ownerUserId ?? me?.userId ?? '', primaryContactId: deal?.primaryContactId ?? '',
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((p) => ({ ...p, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: () => {
      const values = {
        name: f.name, mrr: f.mrr || null, setupFee: f.setupFee || null, ownerUserId: f.ownerUserId,
      };
      if (!deal) return createDeal({ ...values, companyId: f.companyId });
      return updateDeal(deal.id, {
        ...values, contractMonths: f.contractMonths || null, strategyFeeAmount: f.strategyFeeAmount || null,
        primaryContactId: f.primaryContactId || null,
        ...(me?.workspace.trackMargin ? { grossMarginPct: f.grossMarginPct || null } : {}),
      });
    },
    onSuccess: (saved) => {
      for (const k of ['deals', 'deal', 'company', 'companies']) void qc.invalidateQueries({ queryKey: [k] });
      onClose();
      if (!deal) navigate(`/deals/${saved.id}`);
    },
  });

  return (
    <Modal title={deal ? 'Edit deal' : 'New deal'} onClose={onClose} footer={<>
      <Button variant="ghost" onClick={onClose}>Cancel</Button>
      <Button onClick={() => save.mutate()} disabled={save.isPending || (!deal && !f.companyId)}>{save.isPending ? 'Saving…' : 'Save'}</Button>
    </>}>
      <div className="crm-form">
        {!deal && !companyId && (
          <Select label="Company" required placeholder="Choose a company" value={f.companyId} onChange={set('companyId')}
            options={companies.map((c) => ({ value: c.id, label: c.name }))} />
        )}
        <Input label="Deal name" placeholder={deal ? undefined : 'Defaults to the company name'} value={f.name} onChange={set('name')} />
        <div className="crm-grid2">
          <Input label="Monthly retainer (MRR)" inputMode="decimal" placeholder="$" value={f.mrr} onChange={set('mrr')} />
          <Input label="Setup fee (month 1)" inputMode="decimal" placeholder="$" value={f.setupFee} onChange={set('setupFee')} />
          {deal && <>
            <Input label="Term (months)" inputMode="numeric" placeholder="Month to month" value={f.contractMonths} onChange={set('contractMonths')} />
            <Input label="Strategy fee" inputMode="decimal" placeholder="$500" hint="Credited toward the engagement; refunded if lost." value={f.strategyFeeAmount} onChange={set('strategyFeeAmount')} />
            {me?.workspace.trackMargin && <Input label="Gross margin %" inputMode="decimal" value={f.grossMarginPct} onChange={set('grossMarginPct')} />}
          </>}
          <Select label="Owner" options={ownerOptions} value={f.ownerUserId} onChange={set('ownerUserId')} />
          {deal && contacts && (
            <Select label="Primary contact" options={[{ value: '', label: 'None' }, ...contacts.map((c) => ({ value: c.id, label: c.name }))]}
              value={f.primaryContactId} onChange={set('primaryContactId')} />
          )}
        </div>
        <FormError error={save.error} />
      </div>
    </Modal>
  );
}
